/**
 * useScoreHitTesting
 *
 * Geometrie-/Lookup-Teil der interaktiven Vorschau: Klick-Koordinaten → Sheet-Koordinaten,
 * Takt-/Noten-Treffer (hitAt/measureAt/noteAtWithinThreshold) sowie die Identitäts-Lookups über
 * die musikalische Struktur (Taktnummern-Liste, Notenzählung, GraphicalNote/-Measure per
 * Takt+Index). Rein lesend – kein Selektions-State. Sub-Composable von
 * {@link @/composables/useOsmdInteractiveScore}.
 *
 * **Die tragende Invariante: jeder Treffer wird SEITENWEISE aufgelöst (kritisch – nicht auf eine
 * container-globale Rechnung oder auf `GraphicSheet.GetNearestNote` rückbauen).** OSMD-Koordinaten
 * sind seiten-lokal (Herleitung im Modul-Docstring von `osmd/pageSvgs.ts`), und OSMDs eigene
 * `GetNearest*`-Familie durchsucht **alle** Seiten, misst zum BBox-Ursprung des VoiceEntry statt
 * zum Notenkopf, ignoriert das übergebene `maxClickDist` und weitet ihren Radius (5 → 20
 * Einheiten), bis irgendetwas passt (auch `GraphicSheet.domToSvg` fällt aus: hart auf
 * `Backends[0]` verdrahtet, also nur Seite 1). Zusammen trifft ein Klick auf eine hohe
 * Melodienote dadurch eine gleich positionierte Note einer **anderen Seite**. Deshalb: Seite
 * bestimmen → Takt auf dieser Seite bestimmen → nur dort suchen, mit fester Schranke.
 * Regressionstest: `webapp/src/__tests__/scoreHitTestingPages.spec.ts`.
 */
import type {Ref} from 'vue';
import {
    GraphicalMeasure,
    GraphicalNote,
    GraphicalVoiceEntry,
    OpenSheetMusicDisplay,
    unitInPixels
} from 'opensheetmusicdisplay';

import {collectPageSvgs, clientToSheetUnits} from './pageSvgs';
import type {PatternEvent, PatternLane} from './patternMatch';
import {
    candidatesInRect,
    intersectRects,
    nearestNoteAmong,
    normalizedRect,
    preferPrintableNote,
    resolvePageAt,
    type NoteCandidate,
    type PageRect,
} from './selectionLogic';


/** Ein Punkt in Sheet-Einheiten **samt der Seite**, in deren Koordinatenraum er gilt. */
export interface SheetPosition {
    x: number;
    y: number;
    /** 0-basierter Index in `GraphicSheet.MusicPages` bzw. der `<svg>`-Seiten im Container. */
    pageIndex: number;
}

/** Ergebnis von {@link useScoreHitTesting.hitAt}: alles, was ein Maus-Event über die Partitur
 *  hergibt – aus EINER Koordinaten-Transformation und EINEM Takt-Scan. */
export interface ScoreHit {
    pos: SheetPosition | null;
    measure: GraphicalMeasure | null;
    /** Note innerhalb der horizontalen Toleranz, sonst null (= freier Bereich). */
    note: GraphicalNote | null;
}

/** Obergrenze für „Note unter dem Zeiger" in Sheet-Einheiten (Manhattan-Abstand zum Notenkopf).
 *  Großzügig genug für Hilfslinien-Noten und Akkorde, aber endlich – im Gegensatz zu OSMDs
 *  wachsendem Suchradius. */
const NOTE_SEARCH_RADIUS = 8;

/** Horizontale Treffer-Toleranz (Sheet-Einheiten) für „Note angeklickt" vs. „freier Bereich". */
const NOTE_HIT_TOLERANCE_X = 2.0;

/** Horizontale Toleranz (Sheet-Einheiten) des Taktkanten-Doppelklicks ({@link ScoreHitTesting.barlineSideAt}). */
const BARLINE_EDGE_TOLERANCE = 1.5;

export function useScoreHitTesting(
    getOsmd: () => OpenSheetMusicDisplay | null,
    containerRef: Ref<HTMLElement | null>,
) {
    function toSheetCoordinates(event: MouseEvent): SheetPosition | null {
        const osmd = getOsmd();
        const container = containerRef.value;
        if (!container || !osmd) return null;

        const svgs = collectPageSvgs(container);
        if (!svgs.length) {
            // Vor dem ersten Render gibt es keine Seiten – Container-Rechnung als Notnagel
            // (einseitiger Fall, in dem beide Bezugssysteme ohnehin zusammenfallen).
            const rect = container.getBoundingClientRect();
            const scale = unitInPixels * (osmd.zoom || 1);
            return {
                x: (event.clientX - rect.left) / scale,
                y: (event.clientY - rect.top) / scale,
                pageIndex: 0,
            };
        }

        const pageIndex = Math.max(
            resolvePageAt(svgs.map((svg) => svg.getBoundingClientRect()), event.clientX, event.clientY),
            0,
        );
        const svg = svgs[pageIndex] ?? svgs[0]!;
        const {x, y} = clientToSheetUnits(svg, event.clientX, event.clientY, osmd.zoom);
        return {x, y, pageIndex};
    }

    // ============ Traversierung (die EINE Quelle je Ebene) ============

    /** Alle GraphicalMeasures der flachen `MeasureList` (Taktzeilen × Staffs) in
     *  Partiturreihenfolge – die eine Takt-Traversierung, auf der alle Lookups aufbauen. */
    function* allMeasures(): Generator<GraphicalMeasure> {
        for (const measureRow of getOsmd()?.GraphicSheet?.MeasureList ?? []) {
            for (const measure of measureRow ?? []) {
                if (measure) yield measure as GraphicalMeasure;
            }
        }
    }

    /** Alle GraphicalMeasures **einer Seite** (MusicSystems → StaffLines → Measures). Liefert die
     *  Seite nichts (noch nicht gerendert, oder ein Test-Fake ohne `MusicPages`), fällt die
     *  Traversierung auf die flache `MeasureList` zurück – dort ist die Seitenfrage mangels
     *  mehrerer Seiten ohnehin gegenstandslos. */
    function* measuresOnPage(pageIndex: number): Generator<GraphicalMeasure> {
        const page = getOsmd()?.GraphicSheet?.MusicPages?.[pageIndex];
        let yielded = false;
        for (const system of page?.MusicSystems ?? []) {
            for (const staffLine of system?.StaffLines ?? []) {
                for (const measure of staffLine?.Measures ?? []) {
                    if (!measure) continue;
                    yielded = true;
                    yield measure;
                }
            }
        }
        if (yielded) return;
        yield* allMeasures();
    }

    /** Die Noten eines Takts, gruppiert nach **Zählzeit** (= einem `GraphicalStaffEntry`):
     *  Akkordtöne und gleichzeitig klingende Stimmen kommen zusammen heraus. Leere Gruppen fallen
     *  weg, die Reihenfolge innerhalb einer Gruppe ist die der `noteIndex`-Zählung. */
    function* onsetGroupsInMeasure(measure: GraphicalMeasure): Generator<GraphicalNote[]> {
        for (const staffEntry of measure.staffEntries || []) {
            const group: GraphicalNote[] = [];
            for (const voiceEntry of staffEntry.graphicalVoiceEntries || []) {
                for (const note of voiceEntry.notes || []) {
                    if (note) group.push(note as GraphicalNote);
                }
            }
            if (group.length) yield group;
        }
    }

    /** Alle GraphicalNotes eines Takts (StaffEntries → VoiceEntries → Notes) in
     *  Layout-Reihenfolge – die eine Noten-Traversierung; ihre Reihenfolge IST die
     *  `noteIndex`-Zählung (Spiegel von `getNoteInfoFromGraphicalNote`). */
    function* notesInMeasure(measure: GraphicalMeasure): Generator<GraphicalNote> {
        for (const group of onsetGroupsInMeasure(measure)) yield* group;
    }

    /** Derselbe Staff-Schlüssel wie in Auswahl/Highlight (`ParentStaff.idInMusicSheet`). */
    function staffIndexOf(measure: GraphicalMeasure): number {
        return measure.ParentStaff?.idInMusicSheet ?? 0;
    }

    // ============ Geometrie (Event → Takt/Note) ============

    /** Mittelpunkt des Notenkopfs in Sheet-Einheiten. Bevorzugt die BBox-**Ränder** (sie sind beim
     *  Symbol-Bounding-Box relativ zum Anker und dürfen negativ sein); ohne Ränder dient `Size` als
     *  Näherung. */
    function noteCandidate(note: GraphicalNote): NoteCandidate<GraphicalNote> | null {
        const shape = note.PositionAndShape;
        const abs = shape?.AbsolutePosition;
        if (!abs) return null;
        const {BorderLeft, BorderRight, BorderTop, BorderBottom} = shape;
        const width = shape.Size?.width ?? 0;
        const height = shape.Size?.height ?? 0;
        const dx = Number.isFinite(BorderLeft) && Number.isFinite(BorderRight)
            ? (BorderLeft + BorderRight) / 2
            : width / 2;
        const dy = Number.isFinite(BorderTop) && Number.isFinite(BorderBottom)
            ? (BorderTop + BorderBottom) / 2
            : height / 2;
        return {note, x: abs.x + dx, y: abs.y + dy};
    }

    function* noteCandidates(notes: Iterable<GraphicalNote>): Generator<NoteCandidate<GraphicalNote>> {
        for (const note of notes) {
            const candidate = noteCandidate(note);
            if (candidate) yield candidate;
        }
    }

    function measureAtPosition(pos: SheetPosition | null): GraphicalMeasure | null {
        if (!pos || !getOsmd()?.GraphicSheet) return null;
        for (const measure of measuresOnPage(pos.pageIndex)) {
            const shape = measure.PositionAndShape;
            if (!shape) continue;
            const {AbsolutePosition: absPos, Size: size} = shape;
            if (
                pos.x >= absPos.x && pos.x <= absPos.x + size.width &&
                pos.y >= absPos.y && pos.y <= absPos.y + size.height
            ) {
                return measure;
            }
        }
        return null;
    }

    /** Doppelklick-Ziel „Taktkante": liegt der Punkt horizontal nahe genug (±{@link
     *  BARLINE_EDGE_TOLERANCE} Sheet-Einheiten) an der linken bzw. rechten Kante des Takts?
     *  Die linke Kante beginnt hinter `beginInstructionsWidth` (Schlüssel/Vorzeichnung am
     *  Zeilenanfang zählen nicht als Taktstrich). Reine Geometrie – Barlines/Voltas sind in OSMD
     *  VexFlow-Stave-Modifier ohne eigene Hit-Objekte. */
    function barlineSideAt(pos: SheetPosition | null, measure: GraphicalMeasure | null): 'left' | 'right' | null {
        const shape = measure?.PositionAndShape;
        if (!pos || !shape) return null;
        const left = shape.AbsolutePosition.x + (measure!.beginInstructionsWidth ?? 0);
        const right = shape.AbsolutePosition.x + shape.Size.width;
        if (Math.abs(pos.x - left) <= BARLINE_EDGE_TOLERANCE) return 'left';
        if (Math.abs(pos.x - right) <= BARLINE_EDGE_TOLERANCE) return 'right';
        return null;
    }

    /** Die Note unter dem Punkt – zuerst **innerhalb des (bereits aufgelösten) Takts** (dort ist
     *  auch die Notenzeile des Grand Staffs schon eindeutig), sonst auf der getroffenen **Seite**.
     *  Über `maxDistance` hinaus: `null` (= freier Bereich). */
    function closestNoteAt(
        pos: SheetPosition | null,
        measure: GraphicalMeasure | null,
        maxDistance = NOTE_SEARCH_RADIUS,
    ): GraphicalNote | null {
        const osmd = getOsmd();
        if (!pos || !osmd?.GraphicSheet) return null;
        try {
            const notes = measure
                ? notesInMeasure(measure)
                : (function* () {
                    for (const m of measuresOnPage(pos.pageIndex)) yield* notesInMeasure(m);
                })();
            const nearest = nearestNoteAmong(noteCandidates(notes), pos, maxDistance);
            // Versteckte Füll-Pausen der Bass-Träger-Stimme → druckbare Note derselben Zählzeit.
            return preferPrintableNote(nearest);
        } catch (e) {
            console.error('Error finding nearest note:', e);
            return null;
        }
    }

    /** Ein Maus-Event → Position, Takt und Note in EINEM Durchlauf (eine Koordinaten-
     *  Transformation, ein Takt-Scan) – die gemeinsame Basis von Klick-, Hover- und
     *  Kontextmenü-Behandlung. `tolX`: horizontale Treffer-Toleranz zur Notenmitte; vertikal
     *  wird bewusst NICHT geprüft (die Tonhöhe variiert – ein Klick über/unter dem Notenkopf
     *  zählt weiter als Treffer). */
    function hitAt(event: MouseEvent, tolX = NOTE_HIT_TOLERANCE_X): ScoreHit {
        const pos = toSheetCoordinates(event);
        const measure = measureAtPosition(pos);
        const nearest = closestNoteAt(pos, measure);
        let note: GraphicalNote | null = nearest;
        if (pos && nearest) {
            const candidate = noteCandidate(nearest);
            if (candidate && Math.abs(pos.x - candidate.x) > tolX) note = null;
        }
        return {pos, measure, note};
    }

    function measureAt(event: MouseEvent): GraphicalMeasure | null {
        return measureAtPosition(toSheetCoordinates(event));
    }

    /** Nur die Note eines Events (Toleranz-gefiltert wie {@link hitAt}). Wer Takt UND Note
     *  braucht, nimmt `hitAt` – das rechnet die Koordinaten nur einmal. */
    function noteAtWithinThreshold(event: MouseEvent, tolX = NOTE_HIT_TOLERANCE_X): GraphicalNote | null {
        return hitAt(event, tolX).note;
    }

    /**
     * Alle **druckbaren** GraphicalNotes, deren Notenkopf-Mittelpunkt im CLIENT-Rechteck liegt –
     * die Trefferprüfung der Rubber-Band-Auswahl ({@link @/composables/osmd/useScoreDragSelection}).
     *
     * **Seitenweise (kritisch – nicht auf eine container-globale Rechnung rückbauen):**
     * OSMD-Koordinaten sind seiten-lokal (Modul-Docstring oben), ein Rechteck über die
     * Seitengrenze wird deshalb je Seite mit deren `<svg>`-Rect geschnitten und die Schnitt-Ecken
     * gegen GENAU dieses SVG in Sheet-Einheiten umgerechnet – dieselbe Koordinatenbasis
     * (`noteCandidate`-Mittelpunkte) wie Klick und Hover, damit Rechteck-Treffer mit
     * Klick-Treffern deckungsgleich sind. Versteckte Füll-Pausen (Bass-Träger,
     * `drawHiddenNotes: true`) werden wie beim Klick per `preferPrintableNote` umgeleitet;
     * bleibt keine druckbare Alternative, fällt der Treffer weg (ein Rechteck „meint" nie eine
     * unsichtbare Note). Identitäts-Dedup NACH der Umleitung (Akkord-Umleitung + Direkttreffer).
     */
    function notesInClientRect(rect: PageRect): GraphicalNote[] {
        const osmd = getOsmd();
        const container = containerRef.value;
        if (!osmd?.GraphicSheet || !container) return [];

        const out: GraphicalNote[] = [];
        const seen = new Set<GraphicalNote>();
        const collect = (measures: Iterable<GraphicalMeasure>, sheetRect: PageRect) => {
            for (const measure of measures) {
                const shape = measure.PositionAndShape;
                const abs = shape?.AbsolutePosition;
                const size = shape?.Size;
                // Takt-Vorfilter (reine Optimierung, ändert nie die Treffer-Semantik): Noten nur
                // in Takten prüfen, deren Box das Rechteck schneidet. Die Box wird dabei um
                // NOTE_SEARCH_RADIUS aufgeweitet – Hilfslinien-Noten liegen AUSSERHALB der
                // Stave-Box ihres Takts (dieselbe Schranke, die auch der Klick-Treffer nutzt);
                // Kantenberührung zählt, eine Note exakt auf der Taktkante geht nicht verloren.
                if (abs && size && !intersectRects(
                    {
                        left: abs.x - NOTE_SEARCH_RADIUS,
                        top: abs.y - NOTE_SEARCH_RADIUS,
                        right: abs.x + size.width + NOTE_SEARCH_RADIUS,
                        bottom: abs.y + size.height + NOTE_SEARCH_RADIUS,
                    },
                    sheetRect,
                )) continue;
                for (const hit of candidatesInRect(noteCandidates(notesInMeasure(measure)), sheetRect)) {
                    const printable = preferPrintableNote(hit);
                    if (!printable || printable.sourceNote?.PrintObject === false) continue;
                    if (seen.has(printable)) continue;
                    seen.add(printable);
                    out.push(printable);
                }
            }
        };

        const svgs = collectPageSvgs(container);
        if (!svgs.length) {
            // Notnagel wie in `toSheetCoordinates`: vor dem ersten Render (einseitiger Fall).
            const rectC = container.getBoundingClientRect();
            const scale = unitInPixels * (osmd.zoom || 1);
            collect(measuresOnPage(0), normalizedRect(
                (rect.left - rectC.left) / scale, (rect.top - rectC.top) / scale,
                (rect.right - rectC.left) / scale, (rect.bottom - rectC.top) / scale,
            ));
            return out;
        }

        for (let pageIndex = 0; pageIndex < svgs.length; pageIndex++) {
            const svg = svgs[pageIndex]!;
            const clipped = intersectRects(rect, svg.getBoundingClientRect());
            if (!clipped) continue;
            // Die CTM skaliert/verschiebt nur (keine Rotation) – zwei Ecken genügen.
            const a = clientToSheetUnits(svg, clipped.left, clipped.top, osmd.zoom);
            const b = clientToSheetUnits(svg, clipped.right, clipped.bottom, osmd.zoom);
            collect(measuresOnPage(pageIndex), normalizedRect(a.x, a.y, b.x, b.y));
        }
        return out;
    }

    // ============ Identitäts-Lookups (Takt-/Noten-Indizes) ============

    /** Kanonische, eindeutige 0-basierte Taktordinalindizes in Partiturreihenfolge. */
    function getMeasureIndices(): number[] {
        const seen = new Set<number>();
        for (const measure of allMeasures()) seen.add(measureIndexOf(measure));
        return [...seen].sort((a, b) => a - b);
    }

    function measureIndexOf(measure: GraphicalMeasure): number {
        return measure.parentSourceMeasure.measureListIndex;
    }

    /** Gedruckte MusicXML-Taktnummer zu einem ordinalen Index (nur Anzeige/Editor-Kontext). */
    function measureNumberForIndex(measureIndex: number): number | null {
        const measure = findGraphicalMeasureByIndex(measureIndex);
        if (!measure) return null;
        const source = measure.parentSourceMeasure;
        return Number.isFinite(source.MeasureNumberXML)
            ? source.MeasureNumberXML
            : source.getPrintedMeasureNumber();
    }

    /** Sortierte, eindeutige staffIndizes, die in einem Takt vorkommen – je Notenzeile/Part
     *  existiert pro Taktnummer eine eigene `GraphicalMeasure`. */
    function staffIndicesForMeasureIndex(measureIndex: number): number[] {
        const seen = new Set<number>();
        for (const measure of allMeasures()) {
            if (measureIndexOf(measure) === measureIndex) seen.add(staffIndexOf(measure));
        }
        return [...seen].sort((a, b) => a - b);
    }

    /** Sortierte, eindeutige staffIndizes des gesamten Scores (Anzahl > 1 ⇒ mehrteilig). */
    function getAllStaffIndices(): number[] {
        const seen = new Set<number>();
        for (const measure of allMeasures()) seen.add(staffIndexOf(measure));
        return [...seen].sort((a, b) => a - b);
    }

    /** Anzahl der Noten (inkl. Pausen) in einem Takt – Zählung identisch zu
     *  `findGraphicalNoteByMeasureIndex`/`getNoteInfoFromGraphicalNote`, damit `noteIndex`
     *  konsistent bleibt. */
    function countNotesInMeasureIndex(measureIndex: number, staffIndex = 0): number {
        const measure = findGraphicalMeasureByIndex(measureIndex, staffIndex);
        if (!measure || staffIndexOf(measure) !== staffIndex) return 0;
        let count = 0;
        for (const note of notesInMeasure(measure)) {
            void note;
            count++;
        }
        return count;
    }

    /** Ruft `visit` für JEDE gerenderte GraphicalNote der Partitur auf (alle Systeme, alle Takte,
     *  alle Stimmen), in Layout-Reihenfolge – genutzt vom Unspielbar-Highlight, das den ganzen
     *  Score einfärben muss. Bewusst ein Visitor statt einer Liste: die Aufrufer brauchen keine
     *  Zwischenkopie. Der Staff-Schlüssel erlaubt Filtern gegen `NoteInfo.staffIndex`. */
    function forEachGraphicalNote(visit: (note: GraphicalNote, staffIndex: number) => void): void {
        for (const measure of allMeasures()) {
            const staffIndex = staffIndexOf(measure);
            for (const note of notesInMeasure(measure)) visit(note, staffIndex);
        }
    }

    /** Vergleichsschlüssel eines Tons für {@link patternLanes}; `null` = Pause. */
    function patternKeyOf(note: GraphicalNote): string | null {
        const source = note.sourceNote;
        if (!source || source.isRest() || !source.Pitch) return null;
        // `Notehead` ist bei normalen Köpfen undefined (OSMD spart den Speicher).
        const shape = source.Notehead?.Shape ?? 'normal';
        return `${source.Pitch.getHalfTone()}:${shape}`;
    }

    /** Die Partitur als Lanes für die Pattern-Suche (`patternMatch.ts`): je Notenzeile und Stimme
     *  (`staffIndex|VoiceId`) eine chronologische Folge von Events, ein Event je
     *  `GraphicalVoiceEntry` (Akkordtöne zusammen). `allMeasures()` liefert zeilenweise, also
     *  wächst jede Lane in Zeitrichtung. Länge = `sourceNote.Length` des ersten Tons (alle Töne
     *  eines VoiceEntry teilen sie).
     *
     *  **Schlüssel = Halbton + Notenkopfform (kritisch – nicht auf den Halbton allein
     *  rückbauen):** in der Griffschrift-Ausgabe teilen sich Reihe 1 und 3 (bzw. 2 und 4) die
     *  Staff-Positionen (`button_layout.py`), unterschieden nur durch den Kopf (`x`/`diamond`/
     *  `triangle`, `button_model.as_griffschrift`). Ohne die Form fände die Suche dort falsche
     *  Wiederholungen. Die Balgrichtung vergleicht der Schlüssel NICHT – sie steht als Lyric
     *  bzw. `PedalMark` neben der Note, nicht an ihr. In der Eingabe sind die Köpfe normal, der
     *  Schlüssel ist dort faktisch die Tonhöhe.
     *
     *  Bewusst ohne Cache hier: `render()` baut die GraphicalNotes neu, ohne das `GraphicSheet`
     *  auszutauschen – ein Cache am Sheet läge nach Zoom/Edit falsch. */
    function patternLanes(): PatternLane<GraphicalNote>[] {
        const lanes = new Map<string, PatternLane<GraphicalNote>>();
        for (const measure of allMeasures()) {
            const staffIndex = staffIndexOf(measure);
            for (const staffEntry of measure.staffEntries || []) {
                for (const voiceEntry of staffEntry.graphicalVoiceEntries || []) {
                    const notes = (voiceEntry.notes || []).filter(Boolean) as GraphicalNote[];
                    const first = notes[0]?.sourceNote;
                    if (!first) continue;
                    const voiceId = (voiceEntry as GraphicalVoiceEntry).parentVoiceEntry?.ParentVoice?.VoiceId ?? 1;
                    const event: PatternEvent<GraphicalNote> = {
                        length: first.Length?.RealValue ?? 0,
                        notes: notes.map(note => ({note, key: patternKeyOf(note)})),
                    };
                    const key = `${staffIndex}|${voiceId}`;
                    const lane = lanes.get(key);
                    if (lane) lane.push(event);
                    else lanes.set(key, [event]);
                }
            }
        }
        return [...lanes.values()];
    }

    /** GraphicalNote über Taktordinalindex + Staff + **Stimme + Position innerhalb der Stimme**
     *  (`voiceNoteIndex`, Spiegel der `perVoiceCount`-Zählung in `getNoteInfoFromGraphicalNote`).
     *  Dient der Neuableitung des flachen `noteIndex` nach Edits, die Akkordtöne eingefügt/entfernt
     *  haben (`remapSelectionAfterIndexEdits` markiert ihn mit `-1`). */
    function findGraphicalNoteByVoiceIndex(
        measureIndex: number, staffIndex: number, voice: number, voiceNoteIndex: number,
    ): GraphicalNote | null {
        const measure = findGraphicalMeasureByIndex(measureIndex, staffIndex);
        if (!measure || staffIndexOf(measure) !== staffIndex) return null;
        let count = 0;
        for (const staffEntry of measure.staffEntries || []) {
            for (const voiceEntry of staffEntry.graphicalVoiceEntries || []) {
                const voiceId = (voiceEntry as GraphicalVoiceEntry).parentVoiceEntry?.ParentVoice?.VoiceId ?? 1;
                if (voiceId !== voice) continue;
                for (const note of voiceEntry.notes || []) {
                    if (count === voiceNoteIndex) return note as GraphicalNote;
                    count++;
                }
            }
        }
        return null;
    }

    /** GraphicalNote über eindeutigen Taktordinalindex + Staff + flachen Notenindex. */
    function findGraphicalNoteByMeasureIndex(measureIndex: number, noteIndex: number, staffIndex = 0): GraphicalNote | null {
        const measure = findGraphicalMeasureByIndex(measureIndex, staffIndex);
        if (!measure || staffIndexOf(measure) !== staffIndex) return null;
        let index = 0;
        for (const note of notesInMeasure(measure)) {
            if (index === noteIndex) return note;
            index++;
        }
        return null;
    }

    /** Flacher Index-Bereich `[start, end)` aller Noten, die sich mit `noteIndex` **eine Zählzeit**
     *  teilen (Akkordtöne und gleichzeitige Stimmen); `null`, wenn der Takt oder der Index nicht
     *  existiert. Weil `notesInMeasure` je `GraphicalStaffEntry` einen zusammenhängenden Block
     *  liefert, ist der Bereich immer ein Intervall.
     *
     *  **Die Zählzeit wird über den `GraphicalStaffEntry` bestimmt, NICHT über
     *  `AbsolutePosition.x` (kritisch – nicht auf einen Positionsvergleich rückbauen):** OSMD
     *  versetzt die Notenköpfe von **Sekunden** seitlich am Hals, ein x-Vergleich hält einen
     *  Cluster (c/d) deshalb für zwei Zählzeiten – und ein `===` auf Gleitkommazahlen ist
     *  ohnehin brüchig. Konsument: die Zählzeit-Navigation der Gesangstext-Sitzung
     *  (`useScoreSelection.selectNextNote/selectPreviousNote` mit `skipSamePosition`). */
    function onsetGroupBounds(
        measureIndex: number, noteIndex: number, staffIndex = 0,
    ): {start: number; end: number} | null {
        const measure = findGraphicalMeasureByIndex(measureIndex, staffIndex);
        if (!measure || staffIndexOf(measure) !== staffIndex) return null;
        let start = 0;
        for (const group of onsetGroupsInMeasure(measure)) {
            const end = start + group.length;
            if (noteIndex >= start && noteIndex < end) return {start, end};
            start = end;
        }
        return null;
    }

    function findGraphicalMeasureByIndex(measureIndex: number, staffIndex?: number): GraphicalMeasure | null {
        let fallback: GraphicalMeasure | null = null;
        for (const measure of allMeasures()) {
            if (measureIndexOf(measure) !== measureIndex) continue;
            if (staffIndex === undefined) return measure;
            if (staffIndexOf(measure) === staffIndex) return measure;
            fallback ??= measure;
        }
        return fallback;
    }

    return {
        toSheetCoordinates,
        hitAt,
        barlineSideAt,
        measureAt,
        noteAtWithinThreshold,
        notesInClientRect,
        getMeasureIndices,
        measureIndexOf,
        measureNumberForIndex,
        staffIndicesForMeasureIndex,
        getAllStaffIndices,
        countNotesInMeasureIndex,
        forEachGraphicalNote,
        patternLanes,
        findGraphicalNoteByMeasureIndex,
        findGraphicalNoteByVoiceIndex,
        onsetGroupBounds,
        findGraphicalMeasureByIndex,
    } as const;
}

export type ScoreHitTesting = ReturnType<typeof useScoreHitTesting>;
